import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import MainLayout from '../../../../../components/layout/MainLayout';
import { WorkspacePageHeader } from '../../../../../components/layout/WorkspacePage';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { useCurrency } from '../../../../../context/CurrencyContext';
import { PaginatedListLoading } from '../../../../../components/ui/PaginatedListLoading';
import {
  shouldConfirmPreorderDisable,
  validatePreorderFormFields,
} from '../preorderFormValidation';
import {
  DEFAULT_DEPARTMENT_SLUG,
  setWorkspaceDepartmentScope,
  useDepartmentSlug,
} from '../../../../../utils/departmentRouting';
import ProductDetailsDrawer from '../components/ProductDetailsDrawer';
import ProductLabelWorkspace from '../components/ProductLabelWorkspace';
import type { ProductWorkspaceDetail } from '../productLabelTypes';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type CategoryOption = { id: string; name: string; slug: string; departmentId?: string };

type ProductRow = {
  id: string;
  sku: string;
  name: string;
  description?: string | null;
  imageUrl?: string | null;
  baseUnit: 'LB' | 'KG' | 'PACK';
  unitLabel?: string | null;
  pricePerUnit?: number;
  discountPercent?: number | null;
  purchasePricePerUnit?: number | null;
  salePrice?: number | null;
  typicalExpiryDays?: number | null;
  // salePrice and fixedDeliveryFee exist in DB but are no longer used in the backoffice UI.
  currentStockLbs: number;
  isActive: boolean;
  categoryId?: string | null;
  category?: CategoryOption | null;
  isPreorderEnabled?: boolean;
  preorderEta?: string | null;
  preorderDepositType?: 'PERCENT' | 'FIXED_USD' | null;
  preorderDepositValue?: number | null;
  preorderCancellationFeeType?: 'PERCENT' | 'FIXED_USD' | null;
  preorderCancellationFeeValue?: number | null;
  preorderMaxQtyLbs?: number | null;
  hsCode?: string | null;
  countryOfOrigin?: string | null;
};

type ProductsResponse = {
  items: ProductRow[];
  total: number;
  page: number;
  pageSize: number;
};
const PRODUCTS_PAGE_SIZE = 20;
const ALL_SCOPE_GROUP_PAGE_SIZE = 10;

type ProductTranslationsResponse = {
  locale: string;
  productId: string;
  translations: {
    name: string;
    description: string;
    benefits: string;
  };
};

const TRANSLATION_LOCALE_OPTIONS: Array<{ value: string; labelKey: string; fallback: string }> = [
  { value: 'fr', labelKey: 'lang.fr', fallback: 'French (fr)' },
  { value: 'es', labelKey: 'lang.es', fallback: 'Spanish (es)' },
  { value: 'de', labelKey: 'lang.de', fallback: 'German (de)' },
];

function productEffectiveSalePrice(p: Pick<ProductRow, 'salePrice' | 'pricePerUnit'>): number {
  const sale = p.salePrice;
  return sale != null ? Number(sale) : 0;
}

function CatalogFilterSelect({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
}) {
  return (
    <label className="inline-flex h-8 min-w-0 shrink-0 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 shadow-sm">
      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        className="max-w-[7.5rem] cursor-pointer border-0 bg-transparent py-0 pl-0 pr-5 text-xs font-medium text-slate-800 outline-none focus:ring-0"
      >
        {children}
      </select>
    </label>
  );
}

function ProductsPage() {
  const { authorizedFetch } = useAuth();
  const { currency, formatPrice } = useCurrency();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const departmentSlug = useDepartmentSlug() ?? DEFAULT_DEPARTMENT_SLUG;
  const [workspaceScope, setWorkspaceScope] = useState<string>(() => {
    if (typeof window === 'undefined') return '__all__';
    return window.localStorage.getItem('workspace_department_scope') || '__all__';
  });
  const [workspaceDepartments, setWorkspaceDepartments] = useState<
    Array<{ id: string; slug: string; name: string }>
  >([]);
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [allScopeGroupPages, setAllScopeGroupPages] = useState<Record<string, number>>({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enableBlockedForId, setEnableBlockedForId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [departmentAllowedUnitLabels, setDepartmentAllowedUnitLabels] = useState<string[]>([]);
  const [editing, setEditing] = useState<ProductRow | null>(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    imageUrl: '',
    unitLabel: '',
    discountPercent: '',
    categoryId: '',
    isPreorderEnabled: false,
    preorderEta: '',
    preorderDepositType: 'PERCENT' as 'PERCENT' | 'FIXED_USD',
    preorderDepositValue: '',
    preorderCancellationFeeType: 'FIXED_USD' as 'PERCENT' | 'FIXED_USD',
    preorderCancellationFeeValue: '',
    preorderMaxQtyLbs: '',
    hsCode: '',
    countryOfOrigin: '',
  });
  const [saving, setSaving] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [formErrors, setFormErrors] = useState<{
    categoryId?: string;
    preorderEta?: string;
    preorderDepositValue?: string;
    preorderCancellationFeeValue?: string;
    preorderMaxQtyLbs?: string;
  }>({});
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [discountImpact, setDiscountImpact] = useState<{
    currentDiscountPercent: number;
    processingOrdersUsingCurrentDiscount: number;
  } | null>(null);
  const [translationLocale, setTranslationLocale] = useState<string>('fr');
  const [translationForm, setTranslationForm] = useState({
    name: '',
    description: '',
    benefits: '',
  });
  const [translationLoading, setTranslationLoading] = useState(false);
  const [translationSaving, setTranslationSaving] = useState(false);
  const [translationSuccess, setTranslationSuccess] = useState<string | null>(null);
  const [disablePackagesConfirm, setDisablePackagesConfirm] = useState<{
    productId: string;
    message: string;
  } | null>(null);
  const [detailsProductId, setDetailsProductId] = useState<string | null>(null);
  const [labelWorkspaceProduct, setLabelWorkspaceProduct] = useState<ProductWorkspaceDetail | null>(null);

  const [expirySpeedFilter, setExpirySpeedFilter] = useState<'ALL' | 'FAST' | 'SLOW'>('ALL');
  const [priceSort, setPriceSort] = useState<'DEFAULT' | 'PRICE_DESC' | 'PRICE_ASC'>('DEFAULT');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ENABLED' | 'DISABLED'>('ALL');
  const [preorderFilter, setPreorderFilter] = useState<'ALL' | 'ENABLED' | 'DISABLED'>('ALL');
  const [discountFilter, setDiscountFilter] = useState<'ALL' | 'DISCOUNTED' | 'NOT_DISCOUNTED'>('ALL');
  const formSectionRef = useRef<HTMLElement | null>(null);
  const normalizedAllowedUnitLabels = useMemo(
    () => departmentAllowedUnitLabels.map((x) => x.trim().toLowerCase()).filter(Boolean),
    [departmentAllowedUnitLabels],
  );
  const isAllScope = useMemo(() => {
    const scope = String(workspaceScope || '').trim().toLowerCase();
    return !scope || scope === '__all__';
  }, [workspaceScope]);
  const departmentsById = useMemo(
    () =>
      new Map(
        workspaceDepartments.map((dep) => [
          dep.id,
          { name: dep.name || dep.slug, slug: dep.slug },
        ]),
      ),
    [workspaceDepartments],
  );
  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImageUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await authorizedFetch(`${API_BASE}/catalog/products/image`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const message = (body as { message?: string }).message
          || 'Unable to upload image.';
        throw new Error(message);
      }
      const data = (await res.json()) as { path: string };
      const imageUrl = `${API_BASE}${data.path}`;
      setForm((prev) => ({ ...prev, imageUrl }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to upload image.');
    } finally {
      setImageUploading(false);
    }
  };

  const load = React.useCallback(async (pageToLoad: number, q: string) => {
    setLoading(true);
    setError(null);
    try {
      const buildUrl = (targetPage: number, pageSize: number) => {
      const url = new URL(`${API_BASE}/catalog/products`);
        url.searchParams.set('page', String(targetPage));
        url.searchParams.set('pageSize', String(pageSize));
      if (q) url.searchParams.set('q', q);
      url.searchParams.set('includeExpiryStats', 'true');
      if (expirySpeedFilter !== 'ALL') url.searchParams.set('expirySpeed', expirySpeedFilter);
      if (priceSort !== 'DEFAULT') url.searchParams.set('priceSort', priceSort);
      if (statusFilter !== 'ALL') url.searchParams.set('statusFilter', statusFilter);
      if (preorderFilter !== 'ALL') url.searchParams.set('preorderFilter', preorderFilter);
      if (discountFilter !== 'ALL') url.searchParams.set('discounted', discountFilter);
        if (!isAllScope) {
      url.searchParams.set('departmentSlug', departmentSlug);
        }
        return url.toString();
      };

      if (isAllScope) {
        const firstRes = await fetch(buildUrl(1, 100));
        if (!firstRes.ok) {
          throw new Error('Unable to load products.');
        }
        const firstData = (await firstRes.json()) as ProductsResponse;
        const totalPagesForAll = Math.max(
          1,
          Math.ceil(Number(firstData.total || 0) / Number(firstData.pageSize || 100)),
        );
        let allItems = Array.isArray(firstData.items) ? [...firstData.items] : [];

        for (let nextPage = 2; nextPage <= totalPagesForAll; nextPage += 1) {
          const pageRes = await fetch(buildUrl(nextPage, 100));
          if (!pageRes.ok) break;
          const pageData = (await pageRes.json()) as ProductsResponse;
          if (Array.isArray(pageData.items) && pageData.items.length > 0) {
            allItems = allItems.concat(pageData.items);
          }
        }
        setProducts(allItems);
        setTotal(allItems.length);
        setPage(1);
      } else {
        const res = await fetch(buildUrl(pageToLoad, PRODUCTS_PAGE_SIZE));
      if (!res.ok) {
        throw new Error('Unable to load products.');
      }
      const data = (await res.json()) as ProductsResponse;
      setProducts(data.items);
      setTotal(data.total);
      setPage(data.page);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load products.');
    } finally {
      setLoading(false);
    }
  }, [departmentSlug, discountFilter, expirySpeedFilter, isAllScope, preorderFilter, priceSort, statusFilter]);

  useEffect(() => {
    const readScope = () =>
      (typeof window !== 'undefined'
        ? window.localStorage.getItem('workspace_department_scope')
        : null) || '__all__';
    const handleScopeChange = () => {
      setWorkspaceScope(readScope());
    };
    window.addEventListener(
      'workspace-department-scope:change',
      handleScopeChange as EventListener,
    );
    window.addEventListener('storage', handleScopeChange);
    return () => {
      window.removeEventListener(
        'workspace-department-scope:change',
        handleScopeChange as EventListener,
      );
      window.removeEventListener('storage', handleScopeChange);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authorizedFetch(`${API_BASE}/departments/workspace`);
        if (!res.ok || cancelled) return;
        const rows = (await res.json()) as Array<{ id?: string; slug?: string; name?: string }>;
        if (!cancelled) {
          setWorkspaceDepartments(
            (Array.isArray(rows) ? rows : [])
              .map((row) => ({
                id: String(row.id || '').trim(),
                slug: String(row.slug || '').trim().toLowerCase(),
                name: String(row.name || '').trim(),
              }))
              .filter((row) => Boolean(row.id && row.slug)),
          );
        }
      } catch {
        if (!cancelled) setWorkspaceDepartments([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authorizedFetch]);

  useEffect(() => {
    void load(1, '');
  }, [load]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void load(1, search.trim());
    }, 300);
    return () => window.clearTimeout(handle);
  }, [search, load]);

  useEffect(() => {
    (async () => {
      if (isAllScope) {
        setDepartmentAllowedUnitLabels([]);
        setCategories([]);
        return;
      }
      try {
        const depRes = await fetch(`${API_BASE}/departments/public/${encodeURIComponent(departmentSlug)}`);
        if (depRes.ok) {
          const dep = (await depRes.json()) as { productUnitLabels?: string[] };
          setDepartmentAllowedUnitLabels(Array.isArray(dep.productUnitLabels) ? dep.productUnitLabels : []);
        } else {
          setDepartmentAllowedUnitLabels([]);
        }
        const url = new URL(`${API_BASE}/catalog/categories/workspace`);
        url.searchParams.set('departmentSlug', departmentSlug);
        const res = await authorizedFetch(url.toString());
        if (!res.ok) return;
        const data = (await res.json()) as CategoryOption[];
        setCategories(data);
      } catch {
        // ignore
      }
    })();
  }, [authorizedFetch, departmentSlug, isAllScope]);

  useEffect(() => {
    if (isAllScope && showForm) {
      setShowForm(false);
      setEditing(null);
    }
  }, [isAllScope, showForm]);

  useEffect(() => {
    if (normalizedAllowedUnitLabels.length === 0) {
      setForm((prev) => ({ ...prev, unitLabel: '' }));
      return;
    }
    setForm((prev) => {
      const current = prev.unitLabel.trim().toLowerCase();
      if (current && normalizedAllowedUnitLabels.includes(current)) {
        return prev;
      }
      return { ...prev, unitLabel: normalizedAllowedUnitLabels[0] };
    });
  }, [normalizedAllowedUnitLabels]);

  const totalPages = Math.max(1, Math.ceil(total / PRODUCTS_PAGE_SIZE));
  const groupedAllScopeProductsByDepartment = useMemo(() => {
    const groups = new Map<
      string,
      {
        departmentId: string;
        departmentName: string;
        departmentSlug: string;
        products: ProductRow[];
      }
    >();
    products.forEach((product) => {
      const depId = String(product.category?.departmentId || '__unknown__');
      const dep = departmentsById.get(depId);
      const departmentName = dep?.name || 'Unknown department';
      const depSlug = dep?.slug || 'unknown';
      const existing = groups.get(depId);
      if (existing) {
        existing.products.push(product);
      } else {
        groups.set(depId, {
          departmentId: depId,
          departmentName,
          departmentSlug: depSlug,
          products: [product],
        });
      }
    });
    return Array.from(groups.values()).sort((a, b) =>
      a.departmentName.localeCompare(b.departmentName),
    );
  }, [departmentsById, products]);

  useEffect(() => {
    if (!isAllScope) return;
    setAllScopeGroupPages((prev) => {
      const next: Record<string, number> = {};
      groupedAllScopeProductsByDepartment.forEach((group) => {
        const totalGroupPages = Math.max(
          1,
          Math.ceil(group.products.length / ALL_SCOPE_GROUP_PAGE_SIZE),
        );
        const currentPage = Number(prev[group.departmentId] || 1);
        next[group.departmentId] = Math.min(
          Math.max(1, currentPage),
          totalGroupPages,
        );
      });
      return next;
    });
  }, [groupedAllScopeProductsByDepartment, isAllScope]);

  const startCreate = () => {
    setEditing(null);
    setDiscountImpact(null);
    setTranslationSuccess(null);
    setForm({
      name: '',
      description: '',
      imageUrl: '',
      unitLabel: '',
      discountPercent: '',
      categoryId: '',
      isPreorderEnabled: false,
      preorderEta: '',
      preorderDepositType: 'PERCENT',
      preorderDepositValue: '',
      preorderCancellationFeeType: 'FIXED_USD',
      preorderCancellationFeeValue: '',
      preorderMaxQtyLbs: '',
      hsCode: '',
      countryOfOrigin: '',
    });
    setTranslationForm({ name: '', description: '', benefits: '' });
  };

  const workspaceToRow = (p: ProductWorkspaceDetail): ProductRow => ({
    id: p.id,
    sku: p.sku,
    name: p.name,
    description: p.description,
    imageUrl: p.imageUrl,
    baseUnit: (p.baseUnit as ProductRow['baseUnit']) || 'LB',
    unitLabel: p.unitLabel,
    salePrice: p.salePrice != null ? Number(p.salePrice) : null,
    pricePerUnit: p.pricePerUnit != null ? Number(p.pricePerUnit) : undefined,
    discountPercent: p.discountPercent != null ? Number(p.discountPercent) : null,
    currentStockLbs: Number(p.currentStockLbs ?? 0),
    isActive: p.isActive,
    categoryId: p.category?.id ?? null,
    category: p.category
      ? { ...p.category, slug: p.category.slug || '' }
      : null,
    isPreorderEnabled: p.isPreorderEnabled,
    hsCode: p.hsCode,
    countryOfOrigin: p.countryOfOrigin,
  });

  const openLabelWorkspace = async (p: ProductRow | ProductWorkspaceDetail) => {
    setDetailsProductId(null);
    if ('currentStockLbs' in p && 'description' in p) {
      setLabelWorkspaceProduct(p as ProductWorkspaceDetail);
      return;
    }
    try {
      const res = await authorizedFetch(`${API_BASE}/catalog/products/${p.id}/workspace`);
      if (!res.ok) throw new Error('load');
      setLabelWorkspaceProduct((await res.json()) as ProductWorkspaceDetail);
    } catch {
      setError('Unable to open label workspace');
    }
  };

  const startEdit = (row: ProductRow) => {
    setShowForm(true);
    setEditing(row);
    setDiscountImpact(null);
    setTranslationSuccess(null);
    setForm({
      name: row.name,
      description: row.description || '',
      imageUrl: row.imageUrl || '',
      unitLabel: row.unitLabel || '',
      discountPercent: row.discountPercent == null ? '' : String(row.discountPercent),
      categoryId: row.categoryId || '',
      isPreorderEnabled: Boolean(row.isPreorderEnabled),
      preorderEta: row.preorderEta ? String(row.preorderEta).slice(0, 10) : '',
      preorderDepositType: row.preorderDepositType ?? 'PERCENT',
      preorderDepositValue:
        row.preorderDepositValue == null ? '' : String(row.preorderDepositValue),
      preorderCancellationFeeType: row.preorderCancellationFeeType ?? 'FIXED_USD',
      preorderCancellationFeeValue:
        row.preorderCancellationFeeValue == null
          ? ''
          : String(row.preorderCancellationFeeValue),
      preorderMaxQtyLbs:
        row.preorderMaxQtyLbs == null ? '' : String(row.preorderMaxQtyLbs),
      hsCode: row.hsCode || '',
      countryOfOrigin: row.countryOfOrigin || '',
    });
    setTranslationForm({ name: '', description: '', benefits: '' });
    window.requestAnimationFrame(() => {
      formSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  useEffect(() => {
    let cancelled = false;
    if (!editing?.id) {
      setTranslationForm({ name: '', description: '', benefits: '' });
      setTranslationSuccess(null);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      setTranslationLoading(true);
      try {
        const url = new URL(`${API_BASE}/catalog/products/${editing.id}/translations`);
        url.searchParams.set('locale', translationLocale);
        const res = await authorizedFetch(url.toString());
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as ProductTranslationsResponse;
        if (!cancelled) {
          setTranslationForm({
            name: data.translations.name || '',
            description: data.translations.description || '',
            benefits: data.translations.benefits || '',
          });
        }
      } catch {
        if (!cancelled) {
          setTranslationForm({ name: '', description: '', benefits: '' });
        }
      } finally {
        if (!cancelled) setTranslationLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authorizedFetch, editing?.id, translationLocale]);

  useEffect(() => {
    let cancelled = false;
    if (!editing?.id) {
      setDiscountImpact(null);
      return () => { cancelled = true; };
    }
    (async () => {
      try {
        const res = await authorizedFetch(`${API_BASE}/catalog/products/${editing.id}/discount-impact`);
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as {
          currentDiscountPercent: number;
          processingOrdersUsingCurrentDiscount: number;
        };
        if (!cancelled) {
          setDiscountImpact({
            currentDiscountPercent: Number(data.currentDiscountPercent ?? 0),
            processingOrdersUsingCurrentDiscount: Number(data.processingOrdersUsingCurrentDiscount ?? 0),
          });
        }
      } catch {
        if (!cancelled) setDiscountImpact(null);
      }
    })();
    return () => { cancelled = true; };
  }, [authorizedFetch, editing?.id]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});
    setSaving(true);
    setError(null);
    try {
      if (!form.categoryId) {
        setFormErrors({
          categoryId: t('backoffice.products_category_required', 'Category is required.'),
        });
        setSaving(false);
        return;
      }
      const normalizedUnitLabel = String(
        form.unitLabel || normalizedAllowedUnitLabels[0] || '',
      )
        .trim()
        .toLowerCase();
      if (normalizedAllowedUnitLabels.length === 0) {
        setError('No allowed units configured for this department. Ask admin to configure department units first.');
        setSaving(false);
        return;
      }
      if (!normalizedUnitLabel || !normalizedAllowedUnitLabels.includes(normalizedUnitLabel)) {
        setError(`Please select a valid unit. Allowed: ${departmentAllowedUnitLabels.join(', ')}`);
        setSaving(false);
        return;
      }
      const preorderErrors = validatePreorderFormFields({
        isPreorderEnabled: form.isPreorderEnabled,
        preorderEta: form.preorderEta,
        preorderDepositValue: form.preorderDepositValue,
        preorderCancellationFeeValue: form.preorderCancellationFeeValue,
        preorderMaxQtyLbs: form.preorderMaxQtyLbs,
      });
      if (Object.keys(preorderErrors).length > 0) {
        setFormErrors(preorderErrors);
        setSaving(false);
        return;
      }
      if (
        editing &&
        shouldConfirmPreorderDisable({
          previouslyEnabled: Boolean(editing.isPreorderEnabled),
          nextEnabled: Boolean(form.isPreorderEnabled),
        })
      ) {
        const accepted = window.confirm(
          'New preorders stop immediately; process pending preorders before closing workflow. Continue disabling pre-order for this product?',
        );
        if (!accepted) {
          setSaving(false);
          return;
        }
      }
      const payload = {
        ...form,
        unitLabel: normalizedUnitLabel || null,
        discountPercent:
          form.discountPercent === '' ? 0 : Number(form.discountPercent),
        categoryId: form.categoryId,
        preorderEta: form.preorderEta || null,
        preorderDepositValue:
          form.preorderDepositValue === '' ? null : Number(form.preorderDepositValue),
        preorderCancellationFeeValue:
          form.preorderCancellationFeeValue === ''
            ? null
            : Number(form.preorderCancellationFeeValue),
        preorderMaxQtyLbs:
          form.preorderMaxQtyLbs === '' ? null : Number(form.preorderMaxQtyLbs),
        hsCode: form.hsCode.trim() || null,
        countryOfOrigin: form.countryOfOrigin.trim().toUpperCase() || null,
      };
      const url = editing
        ? `${API_BASE}/catalog/products/${editing.id}`
        : `${API_BASE}/catalog/products`;
      const method = editing ? 'PUT' : 'POST';
      const res = await authorizedFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const message = (body as { message?: string }).message
          || 'Unable to save product.';
        throw new Error(message);
      }
      const body = (await res.json()) as {
        preorderDisableWarning?: { message?: string; openPreorders?: number };
      };
      if (body?.preorderDisableWarning?.message) {
        const open = Number(body.preorderDisableWarning.openPreorders ?? 0);
        window.alert(
          open > 0
            ? `${body.preorderDisableWarning.message}\nOpen pre-orders: ${open}`
            : body.preorderDisableWarning.message,
        );
      }
      await load(1, '');
      setEditing(null);
      setShowForm(false);
      setForm({
        name: '',
        description: '',
        imageUrl: '',
        unitLabel: '',
        discountPercent: '',
        categoryId: '',
        isPreorderEnabled: false,
        preorderEta: '',
        preorderDepositType: 'PERCENT',
        preorderDepositValue: '',
        preorderCancellationFeeType: 'FIXED_USD',
        preorderCancellationFeeValue: '',
        preorderMaxQtyLbs: '',
        hsCode: '',
        countryOfOrigin: '',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save product.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row: ProductRow) => {
    // UX: Guide staff before making the request.
    if (!row.isActive) {
      const effective = productEffectiveSalePrice(row);
      if (!Number.isFinite(effective) || effective <= 0) {
        setEnableBlockedForId(row.id);
        return;
      }
    }
    setTogglingId(row.id);
    setError(null);
    setEnableBlockedForId(null);
    try {
      let payload: Record<string, unknown> = { isActive: !row.isActive };
      let res = await authorizedFetch(`${API_BASE}/catalog/products/${row.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok && row.isActive && res.status === 409) {
        const body = await res.json().catch(() => ({} as Record<string, unknown>));
        const code = String((body as { code?: unknown }).code || '');
        if (code === 'PRODUCT_DISABLE_RELATED_PACKAGES_CONFIRM_REQUIRED') {
          const names = Array.isArray((body as { affectedPackages?: unknown }).affectedPackages)
            ? ((body as { affectedPackages: Array<{ name?: string }> }).affectedPackages
                .map((p) => String(p.name || '').trim())
                .filter(Boolean))
            : [];
          const count = Number((body as { affectedPackageCount?: unknown }).affectedPackageCount || names.length || 0);
          const preview = names.slice(0, 5).join(', ');
          const msg = count > 0
            ? `This product is used by ${count} active package(s): ${preview}${count > 5 ? ', ...' : ''}. If you continue, all affected packages will be disabled. Continue?`
            : 'This product is used by active packages. If you continue, all affected packages will be disabled. Continue?';
          setDisablePackagesConfirm({
            productId: row.id,
            message: msg,
          });
          return;
        } else {
          const message = String((body as { message?: unknown }).message || 'Unable to update product.');
          throw new Error(message);
        }
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const message = (body as { message?: string }).message || 'Unable to update product.';
        throw new Error(message);
      }
      await load(page, search.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update product.');
    } finally {
      setTogglingId(null);
    }
  };

  const confirmDisableWithPackages = async () => {
    if (!disablePackagesConfirm) return;
    const productId = disablePackagesConfirm.productId;
    setTogglingId(productId);
    setError(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/catalog/products/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: false, disableRelatedPackages: true }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const message = (body as { message?: string }).message || 'Unable to update product.';
        throw new Error(message);
      }
      setDisablePackagesConfirm(null);
      await load(page, search.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update product.');
    } finally {
      setTogglingId(null);
    }
  };

  const saveTranslations = async () => {
    if (!editing?.id) return;
    setTranslationSaving(true);
    setError(null);
    setTranslationSuccess(null);
    try {
      const res = await authorizedFetch(
        `${API_BASE}/catalog/products/${editing.id}/translations`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            locale: translationLocale,
            name: translationForm.name,
            description: translationForm.description,
            benefits: translationForm.benefits,
          }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const message = (body as { message?: string }).message || 'Unable to save translations.';
        throw new Error(message);
      }
      const data = (await res.json()) as ProductTranslationsResponse;
      setTranslationForm({
        name: data.translations.name || '',
        description: data.translations.description || '',
        benefits: data.translations.benefits || '',
      });
      setTranslationSuccess('Translation saved successfully.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save translations.');
    } finally {
      setTranslationSaving(false);
    }
  };

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={location.pathname}
    >
      <main className="workspace-page min-w-0 overflow-x-hidden">
        <WorkspacePageHeader
          className="shrink-0 border-b border-slate-200 pb-3"
          title={
            showForm
              ? editing
                ? t('backoffice.products_editing_title', {
                    defaultValue: 'Editing: {{name}}',
                    name: editing.name,
                  })
                : t('backoffice.products_create_title', 'New product')
              : t('backoffice.products_title', 'Products & Catalog')
          }
          subtitle={
            showForm
              ? t('backoffice.products_form_subtitle', 'Update product details, translations, and compliance fields.')
              : t(
                  'backoffice.products_subtitle',
                  'Manage the products, pricing and images that power your store.',
                )
          }
          actions={
            <button
              type="button"
              onClick={() => {
                if (isAllScope) return;
                if (!showForm) {
                  startCreate();
                  setShowForm(true);
                  return;
                }
                setEditing(null);
                setShowForm(false);
              }}
              disabled={isAllScope}
              title={
                isAllScope
                  ? 'Select a specific department scope to create a product.'
                  : undefined
              }
              className="shrink-0 rounded-lg bg-brand-600 px-3.5 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {showForm
                ? t('backoffice.products_back_to_catalog', 'Back to catalog')
                : t('backoffice.products_add_button', 'New product')}
            </button>
          }
        />

        {isAllScope && !showForm ? (
          <p className="text-xs font-medium text-amber-700">
            Select a specific department scope to add a product.
          </p>
        ) : null}

        {error ? <p className="text-xs text-red-600">{error}</p> : null}

        {!showForm ? (
          <section className="workspace-panel !p-2.5 sm:!p-3">
            <div className="flex min-w-0 flex-wrap items-center gap-2 lg:flex-nowrap">
              <div className="flex h-8 min-w-[12rem] flex-1 items-center gap-1.5 sm:max-w-xs">
                <input
                  type="search"
                  placeholder={t('backoffice.products_search_placeholder', 'Search by name or SKU')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-full min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-2.5 text-xs text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-1 focus:ring-brand-400/70"
                />
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="h-full shrink-0 rounded-md border border-slate-300 bg-white px-2.5 text-[11px] font-semibold text-slate-600 hover:border-slate-400 hover:text-slate-800"
                >
                  {t('backoffice.products_search_clear', 'Clear')}
                </button>
              </div>
              <div className="hidden h-6 w-px shrink-0 bg-slate-200 lg:block" aria-hidden />
              <div className="flex min-w-0 max-w-full flex-1 flex-wrap items-center gap-1.5 lg:flex-nowrap">
                <CatalogFilterSelect
                  label={t('products_expire_speed', { defaultValue: 'Expire' })}
                  value={expirySpeedFilter}
                  onChange={(v) => setExpirySpeedFilter(v as typeof expirySpeedFilter)}
                >
                  <option value="ALL">{t('common.all', { defaultValue: 'All' })}</option>
                  <option value="FAST">
                    {t('products_expire_too_fast', { defaultValue: 'Fast' })}
                  </option>
                  <option value="SLOW">
                    {t('products_expire_less_fast', { defaultValue: 'Slow' })}
                  </option>
                </CatalogFilterSelect>
                <CatalogFilterSelect
                  label={t('common.price', { defaultValue: 'Price' })}
                  value={priceSort}
                  onChange={(v) => setPriceSort(v as typeof priceSort)}
                >
                  <option value="DEFAULT">{t('common.default', { defaultValue: 'Default' })}</option>
                  <option value="PRICE_DESC">
                    {t('products_price_most_expensive', { defaultValue: 'High → low' })}
                  </option>
                  <option value="PRICE_ASC">
                    {t('products_price_less_expensive', { defaultValue: 'Low → high' })}
                  </option>
                </CatalogFilterSelect>
                <CatalogFilterSelect
                  label={t('common.status', { defaultValue: 'Status' })}
                  value={statusFilter}
                  onChange={(v) => setStatusFilter(v as typeof statusFilter)}
                >
                  <option value="ALL">{t('common.all', { defaultValue: 'All' })}</option>
                  <option value="ENABLED">{t('common.enabled', { defaultValue: 'Enabled' })}</option>
                  <option value="DISABLED">{t('common.disabled', { defaultValue: 'Disabled' })}</option>
                </CatalogFilterSelect>
                <CatalogFilterSelect
                  label={t('backoffice.products_preorder_label', { defaultValue: 'Pre-order' })}
                  value={preorderFilter}
                  onChange={(v) => setPreorderFilter(v as typeof preorderFilter)}
                >
                  <option value="ALL">{t('common.all', { defaultValue: 'All' })}</option>
                  <option value="ENABLED">{t('common.enabled', { defaultValue: 'On' })}</option>
                  <option value="DISABLED">{t('common.disabled', { defaultValue: 'Off' })}</option>
                </CatalogFilterSelect>
                <CatalogFilterSelect
                  label={t('common.discount', { defaultValue: 'Discount' })}
                  value={discountFilter}
                  onChange={(v) => setDiscountFilter(v as typeof discountFilter)}
                >
                  <option value="ALL">{t('common.all', { defaultValue: 'All' })}</option>
                  <option value="DISCOUNTED">
                    {t('common.discounted', { defaultValue: 'Yes' })}
                  </option>
                  <option value="NOT_DISCOUNTED">
                    {t('common.not_discounted', { defaultValue: 'No' })}
                  </option>
                </CatalogFilterSelect>
              </div>
            </div>
          </section>
        ) : null}

        {showForm ? (
        <section ref={formSectionRef} className="workspace-panel">
            <form
              className="grid gap-4 sm:grid-cols-2"
              onSubmit={handleSave}
            >
              <div className="space-y-1.5 text-sm">
                <label className="block text-xs font-medium text-slate-700" htmlFor="name">
                  {t('backoffice.products_name_label', 'Name')}
                  <span className="ml-0.5 text-red-500">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                />
              </div>
              <div className="space-y-1.5 text-sm sm:col-span-2">
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
                  <label className="inline-flex items-center gap-2 text-xs font-semibold text-indigo-900">
                    <input
                      type="checkbox"
                      checked={form.isPreorderEnabled}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, isPreorderEnabled: e.target.checked }))
                      }
                    />
                    Enable pre-order for this product
                  </label>
                  {form.isPreorderEnabled ? (
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <label className="space-y-1 text-xs text-slate-700">
                        <span className="font-medium">
                          Next arrival date <span className="text-red-500">*</span>
                        </span>
                        <input
                          type="date"
                          required={form.isPreorderEnabled}
                          value={form.preorderEta}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, preorderEta: e.target.value }))
                          }
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                        />
                        {formErrors.preorderEta ? (
                          <p className="text-[11px] text-red-500">{formErrors.preorderEta}</p>
                        ) : null}
                      </label>
                      <label className="space-y-1 text-xs text-slate-700">
                        <span className="font-medium">Deposit type <span className="text-red-500">*</span></span>
                        <select
                          value={form.preorderDepositType}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              preorderDepositType: e.target.value as 'PERCENT' | 'FIXED_USD',
                            }))
                          }
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                        >
                          <option value="PERCENT">Percent</option>
                          <option value="FIXED_USD">Fixed USD</option>
                        </select>
                      </label>
                      <label className="space-y-1 text-xs text-slate-700">
                        <span className="font-medium">Deposit value <span className="text-red-500">*</span></span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          required={form.isPreorderEnabled}
                          value={form.preorderDepositValue}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, preorderDepositValue: e.target.value }))
                          }
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                        />
                        {formErrors.preorderDepositValue ? (
                          <p className="text-[11px] text-red-500">
                            {formErrors.preorderDepositValue}
                          </p>
                        ) : null}
                      </label>
                      <label className="space-y-1 text-xs text-slate-700">
                        <span className="font-medium">Cancellation fee type <span className="text-red-500">*</span></span>
                        <select
                          value={form.preorderCancellationFeeType}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              preorderCancellationFeeType: e.target.value as 'PERCENT' | 'FIXED_USD',
                            }))
                          }
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                        >
                          <option value="PERCENT">Percent</option>
                          <option value="FIXED_USD">Fixed USD</option>
                        </select>
                      </label>
                      <label className="space-y-1 text-xs text-slate-700 sm:col-span-2">
                        <span className="font-medium">Cancellation fee value <span className="text-red-500">*</span></span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          required={form.isPreorderEnabled}
                          value={form.preorderCancellationFeeValue}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              preorderCancellationFeeValue: e.target.value,
                            }))
                          }
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                        />
                        {formErrors.preorderCancellationFeeValue ? (
                          <p className="text-[11px] text-red-500">
                            {formErrors.preorderCancellationFeeValue}
                          </p>
                        ) : null}
                      </label>
                      <label className="space-y-1 text-xs text-slate-700 sm:col-span-2">
                        <span className="font-medium">Pre-order max quantity (lbs) <span className="text-red-500">*</span></span>
                        <input
                          type="number"
                          min="0.1"
                          step="0.1"
                          required={form.isPreorderEnabled}
                          value={form.preorderMaxQtyLbs}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              preorderMaxQtyLbs: e.target.value,
                            }))
                          }
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                        />
                        {formErrors.preorderMaxQtyLbs ? (
                          <p className="text-[11px] text-red-500">
                            {formErrors.preorderMaxQtyLbs}
                          </p>
                        ) : null}
                      </label>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="space-y-1.5 text-sm">
                <label className="block text-xs font-medium text-slate-700" htmlFor="hsCode">
                  HS code (international shipping)
                </label>
                <input
                  id="hsCode"
                  value={form.hsCode}
                  onChange={(e) => setForm((prev) => ({ ...prev, hsCode: e.target.value }))}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                  placeholder="e.g. 080440"
                />
              </div>
              <div className="space-y-1.5 text-sm">
                <label className="block text-xs font-medium text-slate-700" htmlFor="countryOfOrigin">
                  Country of origin (ISO-2)
                </label>
                <input
                  id="countryOfOrigin"
                  value={form.countryOfOrigin}
                  onChange={(e) => setForm((prev) => ({ ...prev, countryOfOrigin: e.target.value }))}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                  placeholder="US"
                  maxLength={2}
                />
              </div>
              <div className="space-y-1.5 text-sm sm:col-span-2">
                <label className="block text-xs font-medium text-slate-700" htmlFor="description">
                  {t('backoffice.products_description_label', 'Description')}
                </label>
                <textarea
                  id="description"
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                />
              </div>
              <div className="space-y-1.5 text-sm">
                <label className="block text-xs font-medium text-slate-700" htmlFor="imageFile">
                  {t('backoffice.products_image_label', 'Product image')}
                  <span className="ml-0.5 text-red-500">*</span>
                </label>
                <input
                  id="imageFile"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="block w-full text-xs text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-slate-900 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white hover:file:bg-slate-700"
                />
                {imageUploading && (
                  <p className="mt-1 text-[11px] text-slate-500">
                    {t('backoffice.products_image_uploading', 'Uploading image…')}
                  </p>
                )}
                {form.imageUrl && !imageUploading && (
                  <div className="mt-2 flex items-center gap-2">
                    <img
                      src={form.imageUrl}
                      alt={form.name || 'Product'}
                      className="h-10 w-10 rounded object-cover ring-1 ring-slate-200"
                    />
                    <span className="text-[11px] text-slate-500">
                      {t('backoffice.products_image_uploaded', 'Image selected')}
                    </span>
                  </div>
                )}
              </div>
              <div className="space-y-1.5 text-sm">
                <label className="block text-xs font-medium text-slate-700" htmlFor="unitLabel">
                  Unit label
                  <span className="ml-0.5 text-red-500">*</span>
                </label>
                <select
                  id="unitLabel"
                  value={form.unitLabel}
                  onChange={(e) => {
                    setError(null);
                    setForm((prev) => ({ ...prev, unitLabel: e.target.value }));
                  }}
                  disabled={normalizedAllowedUnitLabels.length === 0}
                  required
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                >
                  {normalizedAllowedUnitLabels.length === 0 ? (
                    <option value="">No units configured</option>
                  ) : null}
                  {departmentAllowedUnitLabels.map((label) => (
                    <option key={label} value={label.toLowerCase()}>
                      {label}
                    </option>
                  ))}
                </select>
                {departmentAllowedUnitLabels.length > 0 ? (
                  <p className="text-[11px] text-slate-500">
                    Managed by admin for this department: {departmentAllowedUnitLabels.join(', ')}
                  </p>
                ) : (
                  <p className="text-[11px] text-amber-700">
                    Admin must configure department units before products can be saved.
                  </p>
                )}
              </div>
              <div className="space-y-1.5 text-sm">
                <label className="block text-xs font-medium text-slate-700" htmlFor="categoryId">
                  {t('backoffice.products_category_label', 'Category')}
                  <span className="ml-0.5 text-red-500">*</span>
                </label>
                <select
                  id="categoryId"
                  value={form.categoryId}
                  onChange={(e) => setForm((prev) => ({ ...prev, categoryId: e.target.value }))}
                  required
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                >
                  <option value="">—</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {formErrors.categoryId && (
                  <p className="mt-1 text-[11px] text-red-500">
                    {formErrors.categoryId}
                  </p>
                )}
              </div>
              <div className="space-y-1.5 text-sm">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Pricing
                  </div>
                  <div className="mt-1 grid gap-1 text-xs text-slate-700">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-slate-600">Purchase price (auto)</span>
                      <span className="font-semibold text-slate-900">
                        {editing?.purchasePricePerUnit != null && Number(editing.purchasePricePerUnit) > 0
                          ? `${currency} ${formatPrice(Number(editing.purchasePricePerUnit))}`
                          : '—'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-slate-600">Sale price (set in Inventory)</span>
                      <span className="font-semibold text-slate-900">
                        {editing
                          ? (() => {
                              const sale = editing.salePrice ?? editing.pricePerUnit;
                              return sale != null && Number(sale) > 0
                                ? `${currency} ${formatPrice(Number(sale))}`
                                : '—';
                            })()
                          : '—'}
                      </span>
                    </div>
                  </div>
                  <p className="mt-2 text-[11px] text-slate-500">
                    Prices are managed from Purchases / Purchase orders (purchase price) and Inventory (sale price).
                  </p>
                </div>
              </div>
              <div className="space-y-1.5 text-sm">
                <label className="block text-xs font-medium text-slate-700" htmlFor="discountPercent">
                  {t('backoffice.products_discount_label', 'Discount (%)')}
                </label>
                <input
                  id="discountPercent"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.discountPercent}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, discountPercent: e.target.value }))
                  }
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                />
                {editing ? (
                  <div className="space-y-1 text-[11px] text-slate-500">
                    <p>This affects new orders only. In-flight orders keep the price snapshot used at checkout.</p>
                    {discountImpact ? (
                      <p>
                        Currently <span className="font-semibold text-slate-700">{discountImpact.processingOrdersUsingCurrentDiscount}</span>{' '}
                        processing order(s) are using the current discount ({discountImpact.currentDiscountPercent}%).
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div className="flex items-end gap-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center justify-center rounded-md bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/80 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving
                    ? t('backoffice.products_saving', 'Saving…')
                    : editing
                      ? t('backoffice.products_update_button', 'Update product')
                      : t('backoffice.products_create_button', 'Create product')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditing(null);
                    setShowForm(false);
                  }}
                  className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:border-slate-400"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
              </div>
              {editing ? (
                <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3 sm:col-span-2">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Dynamic content translations
                    </h3>
                    <select
                      value={translationLocale}
                      onChange={(e) => {
                        setTranslationLocale(e.target.value);
                        setTranslationSuccess(null);
                      }}
                      className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-800 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                    >
                      {TRANSLATION_LOCALE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {t(opt.labelKey, { defaultValue: opt.fallback })}
                        </option>
                      ))}
                    </select>
                  </div>
                  {translationLoading ? (
                    <p className="text-xs text-slate-500">Loading translations…</p>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5 text-sm">
                        <label className="block text-xs font-medium text-slate-700">Localized name</label>
                        <input
                          type="text"
                          value={translationForm.name}
                          onChange={(e) =>
                            setTranslationForm((prev) => ({ ...prev, name: e.target.value }))
                          }
                          onInput={() => setTranslationSuccess(null)}
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                        />
                      </div>
                      <div className="space-y-1.5 text-sm sm:col-span-2">
                        <label className="block text-xs font-medium text-slate-700">Localized description</label>
                        <textarea
                          rows={3}
                          value={translationForm.description}
                          onChange={(e) =>
                            setTranslationForm((prev) => ({ ...prev, description: e.target.value }))
                          }
                          onInput={() => setTranslationSuccess(null)}
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                        />
                      </div>
                      <div className="space-y-1.5 text-sm sm:col-span-2">
                        <label className="block text-xs font-medium text-slate-700">Localized benefits</label>
                        <textarea
                          rows={2}
                          value={translationForm.benefits}
                          onChange={(e) =>
                            setTranslationForm((prev) => ({ ...prev, benefits: e.target.value }))
                          }
                          onInput={() => setTranslationSuccess(null)}
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <button
                          type="button"
                          disabled={translationSaving}
                          onClick={saveTranslations}
                          className="inline-flex items-center justify-center rounded-md border border-brand-500 bg-white px-4 py-2 text-xs font-semibold text-brand-700 hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {translationSaving ? 'Saving translation…' : 'Save translation'}
                        </button>
                        {translationSuccess ? (
                          <p className="mt-2 text-xs font-medium text-emerald-700">
                            {translationSuccess}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </form>
        </section>
        ) : null}

        {!showForm ? (
        <section className="workspace-panel !p-0">
          <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-3 py-2.5 sm:px-4">
            <h2 className="text-sm font-semibold text-slate-900">
              {t('backoffice.products_table_title', 'Product catalog')}
            </h2>
            {!loading ? (
              <span className="shrink-0 text-xs text-slate-500">
                {total} {total === 1 ? 'product' : 'products'}
              </span>
            ) : null}
          </div>
          {loading ? (
            <PaginatedListLoading
              title={t('backoffice.products_loading', 'Loading products…')}
              skeletonRows={5}
            />
          ) : products.length === 0 ? (
            <p className="px-3 py-6 text-sm text-slate-600 sm:px-4">
              {t('backoffice.products_empty', 'No products yet. Create your first product above.')}
            </p>
          ) : (
            <>
              {!isAllScope ? (
              <div className="products-catalog-scroll relative min-w-0 max-w-full overflow-x-auto overscroll-x-contain">
                <table className="w-max min-w-full text-left text-xs">
                  <thead className="border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="whitespace-nowrap px-3 py-2.5 font-semibold">{t('backoffice.products_table_col_status', 'Status')}</th>
                      <th className="whitespace-nowrap px-3 py-2.5 font-semibold">{t('backoffice.products_table_col_action', 'Action')}</th>
                      <th className="whitespace-nowrap px-3 py-2.5 font-semibold">{t('backoffice.products_table_col_sku', 'SKU')}</th>
                      <th className="whitespace-nowrap px-3 py-2.5 font-semibold">
                        {t('backoffice.products_table_col_name', 'Name')}
                      </th>
                      <th className="whitespace-nowrap px-3 py-2.5 font-semibold">{t('backoffice.products_table_col_category', 'Category')}</th>
                      <th className="whitespace-nowrap px-3 py-2.5 font-semibold">Purchase / unit</th>
                      <th className="whitespace-nowrap px-3 py-2.5 font-semibold">Sale / unit</th>
                      <th className="whitespace-nowrap px-3 py-2.5 font-semibold">Expiry</th>
                      <th className="whitespace-nowrap px-3 py-2.5 font-semibold">
                        {t('backoffice.products_table_col_preorder', 'Pre-order')}
                      </th>
                      <th className="whitespace-nowrap px-3 py-2.5 font-semibold">
                        {t('backoffice.products_table_col_discount', 'Discount')}
                      </th>
                      <th className="whitespace-nowrap px-3 py-2.5 font-semibold">
                        {t('backoffice.products_table_col_stock', 'Stock (lb)')}
                      </th>
                      <th className="px-3 py-2.5" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {products.map((p) => (
                      <tr key={p.id}>
                        <td className="px-3 py-2.5">
                          <span
                            className={
                              p.isActive
                                ? 'inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700'
                                : 'inline-flex rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-rose-700'
                            }
                          >
                            {p.isActive
                              ? t('backoffice.products_active', 'Active')
                              : t('backoffice.products_disabled', 'Disabled')}
                          </span>
                        </td>
                        <td className="min-w-[6.5rem] px-3 py-2.5 align-top">
                          <div className="space-y-1">
                            <button
                              type="button"
                              disabled={togglingId === p.id}
                              onClick={() => toggleActive(p)}
                              className={
                                p.isActive
                                  ? 'inline-flex items-center justify-center rounded-lg bg-rose-600 px-3 py-1.5 text-[12px] font-semibold text-white shadow-sm transition hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-60'
                                  : 'inline-flex items-center justify-center rounded-lg bg-emerald-600 px-3 py-1.5 text-[12px] font-semibold text-white shadow-sm transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60'
                              }
                            >
                              {p.isActive
                                ? t('backoffice.products_disable', 'Disable')
                                : t('backoffice.products_enable', 'Enable')}
                            </button>
                            {enableBlockedForId === p.id ? (
                              <div className="max-w-[220px] space-y-1">
                                <p className="text-[11px] font-medium text-rose-600">
                                  {t(
                                    'backoffice.products_enable_requires_inventory_price',
                                    'Before enabling, set a Sale price in Inventory.',
                                  )}
                                </p>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setWorkspaceDepartmentScope(departmentSlug);
                                    navigate('/workspace/inventory');
                                  }}
                                  className="text-[11px] font-semibold text-brand-700 underline underline-offset-2 hover:text-brand-600"
                                >
                                  {t('backoffice.products_open_inventory', 'Open Inventory')}
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-slate-700">{p.sku}</td>
                        <td className="px-3 py-2.5 text-slate-800">{p.name}</td>
                        <td className="px-3 py-2.5 text-slate-700">{p.category?.name ?? '—'}</td>
                        <td className="px-3 py-2.5 text-slate-700">
                          {p.purchasePricePerUnit != null ? (
                            <>
                              {Number(p.purchasePricePerUnit) > 0
                                ? `${currency} ${formatPrice(Number(p.purchasePricePerUnit))} / ${p.unitLabel || p.baseUnit}`
                                : '—'}
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-slate-700">
                          {(() => {
                            const sale = p.salePrice ?? p.pricePerUnit;
                            return sale != null && Number(sale) > 0
                              ? `${currency} ${formatPrice(Number(sale))} / ${p.unitLabel || p.baseUnit}`
                              : '—';
                          })()}
                        </td>
                        <td className="px-3 py-2.5 text-slate-700">
                          {p.typicalExpiryDays != null
                            ? `${p.typicalExpiryDays.toFixed(0)}d`
                            : '—'}
                        </td>
                        <td className="px-3 py-2.5 align-top">
                          {p.isPreorderEnabled ? (
                            <div className="flex flex-col gap-1">
                              <span className="inline-flex w-fit rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700">
                                {t('backoffice.products_preorder_enabled', 'Enabled')}
                              </span>
                              <span className="text-[10px] text-slate-500">
                                {p.preorderEta
                                  ? `ETA ${String(p.preorderEta).slice(0, 10)}`
                                  : t('backoffice.products_preorder_eta_missing', 'ETA not set')}
                              </span>
                            </div>
                          ) : (
                            <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                              {t('backoffice.products_preorder_disabled', 'Disabled')}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-slate-700">
                          {Number(p.discountPercent ?? 0) > 0 ? `${Number(p.discountPercent).toFixed(2)}%` : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-slate-700">
                          {p.currentStockLbs.toFixed(2)}
                        </td>
                        <td className="px-3 py-2.5 align-top">
                          <div className="flex flex-wrap gap-1">
                            <button
                              type="button"
                              onClick={() => setDetailsProductId(p.id)}
                              className="border border-slate-300 px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-slate-700 hover:border-slate-400"
                            >
                              {t('backoffice.products_action_view', { defaultValue: 'View' })}
                            </button>
                            <button
                              type="button"
                              onClick={() => openLabelWorkspace(p)}
                              className="border border-sky-200 bg-sky-50 px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-sky-800 hover:border-sky-300"
                            >
                              {t('backoffice.products_action_label', { defaultValue: 'Label' })}
                            </button>
                            <button
                              type="button"
                              onClick={() => startEdit(p)}
                              className="border border-slate-300 px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-slate-700 hover:border-slate-400"
                            >
                              {t('backoffice.products_action_edit', 'Edit')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              ) : (
                <div className="space-y-4">
                  {groupedAllScopeProductsByDepartment.map((group) => {
                    const groupPage = Math.max(1, Number(allScopeGroupPages[group.departmentId] || 1));
                    const groupTotalPages = Math.max(
                      1,
                      Math.ceil(group.products.length / ALL_SCOPE_GROUP_PAGE_SIZE),
                    );
                    const groupPageSafe = Math.min(groupPage, groupTotalPages);
                    const groupSliceStart = (groupPageSafe - 1) * ALL_SCOPE_GROUP_PAGE_SIZE;
                    const groupProductsPaged = group.products.slice(
                      groupSliceStart,
                      groupSliceStart + ALL_SCOPE_GROUP_PAGE_SIZE,
                    );
                    return (
                      <div
                        key={group.departmentId}
                        className="workspace-stat-card"
                      >
                      <div className="mb-3 flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-slate-900/5 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                          {group.departmentName}
                        </span>
                        <span className="text-[11px] text-slate-500">/{group.departmentSlug}</span>
                        <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-800">
                          {group.products.length} product{group.products.length > 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="products-catalog-scroll min-w-0 max-w-full overflow-x-auto">
                        <table className="w-max min-w-full text-left text-xs sm:text-sm">
                          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                            <tr>
                              <th className="whitespace-nowrap px-3 py-2.5">{t('backoffice.products_table_col_status', 'Status')}</th>
                              <th className="whitespace-nowrap px-3 py-2.5">{t('backoffice.products_table_col_action', 'Action')}</th>
                              <th className="whitespace-nowrap px-3 py-2.5">{t('backoffice.products_table_col_sku', 'SKU')}</th>
                              <th className="whitespace-nowrap px-3 py-2.5">
                                {t('backoffice.products_table_col_name', 'Name')}
                              </th>
                              <th className="whitespace-nowrap px-3 py-2.5">{t('backoffice.products_table_col_category', 'Category')}</th>
                              <th className="whitespace-nowrap px-3 py-2.5">Purchase price / unit</th>
                              <th className="whitespace-nowrap px-3 py-2.5">Sale price / unit</th>
                              <th className="whitespace-nowrap px-3 py-2.5">Typical expiry (days)</th>
                              <th className="whitespace-nowrap px-3 py-2.5">
                                {t('backoffice.products_table_col_preorder', 'Pre-order')}
                              </th>
                              <th className="whitespace-nowrap px-3 py-2.5">
                                {t('backoffice.products_table_col_discount', 'Discount')}
                              </th>
                              <th className="whitespace-nowrap px-3 py-2.5">
                                {t('backoffice.products_table_col_stock', 'Stock (lb)')}
                              </th>
                              <th className="whitespace-nowrap px-3 py-2.5" />
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {groupProductsPaged.map((p) => (
                              <tr key={p.id}>
                                <td className="px-3 py-2.5">
                                  <span
                                    className={
                                      p.isActive
                                        ? 'inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700'
                                        : 'inline-flex rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-rose-700'
                                    }
                                  >
                                    {p.isActive
                                      ? t('backoffice.products_active', 'Active')
                                      : t('backoffice.products_disabled', 'Disabled')}
                                  </span>
                                </td>
                                <td className="min-w-[6.5rem] px-3 py-2.5 align-top">
                                  <div className="space-y-1">
                                    <button
                                      type="button"
                                      disabled={togglingId === p.id}
                                      onClick={() => toggleActive(p)}
                                      className={
                                        p.isActive
                                          ? 'inline-flex items-center justify-center rounded-lg bg-rose-600 px-3 py-1.5 text-[12px] font-semibold text-white shadow-sm transition hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-60'
                                          : 'inline-flex items-center justify-center rounded-lg bg-emerald-600 px-3 py-1.5 text-[12px] font-semibold text-white shadow-sm transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60'
                                      }
                                    >
                                      {p.isActive
                                        ? t('backoffice.products_disable', 'Disable')
                                        : t('backoffice.products_enable', 'Enable')}
                                    </button>
                                    {enableBlockedForId === p.id ? (
                                      <div className="max-w-[220px] space-y-1">
                                        <p className="text-[11px] font-medium text-rose-600">
                                          {t(
                                            'backoffice.products_enable_requires_inventory_price',
                                            'Before enabling, set a Sale price in Inventory.',
                                          )}
                                        </p>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setWorkspaceDepartmentScope(group.departmentSlug);
                                            navigate('/workspace/inventory');
                                          }}
                                          className="text-[11px] font-semibold text-brand-700 underline underline-offset-2 hover:text-brand-600"
                                        >
                                          {t('backoffice.products_open_inventory', 'Open Inventory')}
                                        </button>
                                      </div>
                                    ) : null}
                                  </div>
                                </td>
                                <td className="px-3 py-2.5 text-slate-700">{p.sku}</td>
                                <td className="px-3 py-2.5 text-slate-800">{p.name}</td>
                                <td className="px-3 py-2.5 text-slate-700">{p.category?.name ?? '—'}</td>
                                <td className="px-3 py-2.5 text-slate-700">
                                  {p.purchasePricePerUnit != null ? (
                                    <>
                                      {Number(p.purchasePricePerUnit) > 0
                                        ? `${currency} ${formatPrice(Number(p.purchasePricePerUnit))} / ${p.unitLabel || p.baseUnit}`
                                        : '—'}
                                    </>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td className="px-3 py-2.5 text-slate-700">
                                  {(() => {
                                    const sale = p.salePrice ?? p.pricePerUnit;
                                    return sale != null && Number(sale) > 0
                                      ? `${currency} ${formatPrice(Number(sale))} / ${p.unitLabel || p.baseUnit}`
                                      : '—';
                                  })()}
                                </td>
                                <td className="px-3 py-2.5 text-slate-700">
                                  {p.typicalExpiryDays != null
                                    ? `${p.typicalExpiryDays.toFixed(0)}d`
                                    : '—'}
                                </td>
                                <td className="px-3 py-2.5 align-top">
                                  {p.isPreorderEnabled ? (
                                    <div className="flex flex-col gap-1">
                                      <span className="inline-flex w-fit rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700">
                                        {t('backoffice.products_preorder_enabled', 'Enabled')}
                                      </span>
                                      <span className="text-[10px] text-slate-500">
                                        {p.preorderEta
                                          ? `ETA ${String(p.preorderEta).slice(0, 10)}`
                                          : t('backoffice.products_preorder_eta_missing', 'ETA not set')}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                                      {t('backoffice.products_preorder_disabled', 'Disabled')}
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2.5 text-slate-700">
                                  {Number(p.discountPercent ?? 0) > 0 ? `${Number(p.discountPercent).toFixed(2)}%` : '—'}
                                </td>
                                <td className="px-3 py-2.5 text-slate-700">
                                  {p.currentStockLbs.toFixed(2)}
                                </td>
                                <td className="px-3 py-2.5 align-top">
                                  <div className="flex flex-wrap gap-1">
                                    <button
                                      type="button"
                                      onClick={() => setDetailsProductId(p.id)}
                                      className="border border-slate-300 px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-slate-700 hover:border-slate-400"
                                    >
                                      {t('backoffice.products_action_view', { defaultValue: 'View' })}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => openLabelWorkspace(p)}
                                      className="border border-sky-200 bg-sky-50 px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-sky-800 hover:border-sky-300"
                                    >
                                      {t('backoffice.products_action_label', { defaultValue: 'Label' })}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => startEdit(p)}
                                      className="border border-slate-300 px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-slate-700 hover:border-slate-400"
                                    >
                                      {t('backoffice.products_action_edit', 'Edit')}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {groupTotalPages > 1 ? (
                        <div className="mt-3 flex items-center justify-end gap-2 text-[11px] text-slate-600">
                          <button
                            type="button"
                            disabled={groupPageSafe <= 1}
                            onClick={() =>
                              setAllScopeGroupPages((prev) => ({
                                ...prev,
                                [group.departmentId]: Math.max(1, groupPageSafe - 1),
                              }))
                            }
                            className="rounded-full border border-slate-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {t('backoffice.products_prev_page', 'Prev')}
                          </button>
                          <span>
                            {groupPageSafe} / {groupTotalPages}
                          </span>
                          <button
                            type="button"
                            disabled={groupPageSafe >= groupTotalPages}
                            onClick={() =>
                              setAllScopeGroupPages((prev) => ({
                                ...prev,
                                [group.departmentId]: Math.min(groupTotalPages, groupPageSafe + 1),
                              }))
                            }
                            className="rounded-full border border-slate-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {t('backoffice.products_next_page', 'Next')}
                          </button>
                        </div>
                      ) : null}
                    </div>
                    );
                  })}
                </div>
              )}
              {totalPages > 1 && !isAllScope && (
                <div className="mt-4 flex flex-col items-start gap-2 text-xs text-slate-600 sm:flex-row sm:items-center sm:justify-end">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => load(page - 1, search)}
                    className="rounded-full border border-slate-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {t('backoffice.products_prev_page', 'Prev')}
                  </button>
                  <span>
                    {page} / {totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => load(page + 1, search)}
                    className="rounded-full border border-slate-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {t('backoffice.products_next_page', 'Next')}
                  </button>
                </div>
              )}
            </>
          )}
        </section>
        ) : null}
        {disablePackagesConfirm ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 px-4">
            <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
              <h2 className="text-base font-semibold text-slate-900">
                {t('backoffice.products_disable_confirm_title', 'Confirm disable')}
              </h2>
              <p className="mt-3 text-sm text-slate-600">
                {disablePackagesConfirm.message}
              </p>
              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setDisablePackagesConfirm(null)}
                  disabled={togglingId === disablePackagesConfirm.productId}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  type="button"
                  onClick={confirmDisableWithPackages}
                  disabled={togglingId === disablePackagesConfirm.productId}
                  className="rounded-lg bg-rose-600 px-4 py-2 text-xs font-semibold text-white hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {t('common.continue', 'Continue')}
                </button>
              </div>
            </div>
          </div>
        ) : null}
        <ProductDetailsDrawer
          productId={detailsProductId}
          onClose={() => setDetailsProductId(null)}
          onEdit={(p) => {
            setDetailsProductId(null);
            startEdit(workspaceToRow(p));
          }}
          onOpenLabel={(p) => void openLabelWorkspace(p)}
          onProductUpdated={() => void load(page, search)}
          authorizedFetch={authorizedFetch}
          formatPrice={formatPrice}
          currency={currency}
        />
        {labelWorkspaceProduct ? (
          <ProductLabelWorkspace
            product={labelWorkspaceProduct}
            onClose={() => setLabelWorkspaceProduct(null)}
            authorizedFetch={authorizedFetch}
            formatPrice={formatPrice}
            currency={currency}
          />
        ) : null}
      </main>
    </MainLayout>
  );
}

export default ProductsPage;

